import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Tabs,
  Typography,
  Breadcrumb,
  Spin,
  message,
  Button,
} from 'antd';
import {
  HomeOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  CreditCardOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { CompanyService } from '../../services/api/companyService';
import { Company } from '../../types/company';
import { CompanyProfileTab } from './CompanyProfileTab';
import { CompanyUsersTab } from './CompanyUsersTab';
import { CompanySubscriptionTab } from './CompanySubscriptionTab';
import { CompanyBillingTab } from './CompanyBillingTab';
import { CompanyProductModelsTab } from './CompanyProductModelsTab';

const { Title } = Typography;

export const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  // Get activeTab from location state, default to 'profile'
  const [activeTab, setActiveTab] = useState((location.state as any)?.activeTab || 'profile');

  useEffect(() => {
    if (id) {
      loadCompany();
    }
  }, [id]);

  // Update activeTab if location state changes
  useEffect(() => {
    if ((location.state as any)?.activeTab) {
      setActiveTab((location.state as any).activeTab);
    }
  }, [location.state]);

  const loadCompany = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const companyData = await CompanyService.getById(id);
      if (companyData) {
        setCompany(companyData);
      } else {
        message.error('Company not found');
        navigate('/company-management');
      }
    } catch (error) {
      console.error('Error loading company:', error);
      message.error('Failed to load company');
      navigate('/company-management');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyUpdated = () => {
    loadCompany();
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!company) {
    return null;
  }

  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <SettingOutlined />
          Company Profile
        </span>
      ),
      children: (
        <CompanyProfileTab company={company} onUpdated={handleCompanyUpdated} />
      ),
    },
    {
      key: 'users',
      label: (
        <span>
          <TeamOutlined />
          Company Users
        </span>
      ),
      children: <CompanyUsersTab companyId={company.id} />,
    },
    {
      key: 'subscriptions',
      label: (
        <span>
          <CreditCardOutlined />
          Subscriptions
        </span>
      ),
      children: <CompanySubscriptionTab companyId={company.id} />,
    },
    {
      key: 'billing',
      label: (
        <span>
          <CreditCardOutlined />
          Billing
        </span>
      ),
      children: <CompanyBillingTab companyId={company.id} readOnly />,
    },
    {
      key: 'models',
      label: 'Product models',
      children: <CompanyProductModelsTab companyId={company.id} companyName={company.name} />,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumb
        style={{ marginBottom: '16px' }}
        items={[
          {
            href: '/company-management',
            title: (
              <>
                <HomeOutlined />
                <span>Company Management</span>
              </>
            ),
          },
          {
            title: company.name,
          },
        ]}
      />

      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>{company.name}</Title>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/company-management')}
        >
          Back to Companies
        </Button>
      </div>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>
    </div>
  );
};

